import {
  FRAME_LAYOUT_AUTO,
  PADDING_CHOICES,
  resolveFrameLayout,
  TRACK_CHOICES,
  type BoardTheme,
  type Frame,
} from '@praesto/shared'
import { ColorPicker } from '../components/ColorPicker.js'
import { useKeepInView } from '../components/keepInView.js'
import { useText } from '../i18n.js'
import type { CameraStore } from './CameraStore.js'

const BORDER_WIDTHS = [0, 1, 2, 4, 8, 16]

/**
 * Gestaltung eines Frames: Füllung und Rahmen.
 *
 * Die Stärke steht in Board-Einheiten, nicht in Bildschirmpixeln — ein
 * gestalteter Rahmen ist Inhalt und soll beim Zoomen mitwachsen. Die Leiste
 * selbst rechnet den Zoom dagegen heraus und bleibt gleich groß.
 *
 * Sie hängt unter dem Frame und rückt nur dann ins Fenster, wenn sie sonst
 * darunter verschwände — siehe `useKeepInView`.
 */
export function FrameOptions({
  frame,
  theme,
  camera,
  onPatch,
  onSaveTemplate,
  scaleContent,
  onToggleScaleContent,
}: {
  frame: Frame
  theme: BoardTheme
  camera: CameraStore
  onPatch: (id: string, patch: Partial<Frame>) => void
  /** Fehlt in Ansichten ohne Schreibrecht, etwa der Gast-Vorschau. */
  onSaveTemplate?: (frame: Frame) => void
  /** Nimmt der Frame beim Größenändern seinen Inhalt mit? */
  scaleContent: boolean
  onToggleScaleContent?: () => void
}): React.ReactElement {
  // Was „automatisch" gerade bedeutet, steht in der Auswahl mit drin: Sonst
  // wählt man blind zwischen einer Vorgabe und einer Zahl, ohne zu sehen, dass
  // beide dasselbe meinen können.
  const t = useText()
  const resolved = resolveFrameLayout(frame)
  const ref = useKeepInView<HTMLDivElement>(camera)

  return (
    <div
      className="frame-options"
      ref={ref}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <span className="frame-options-label">{t('frame.fill')}</span>
      <ColorPicker
        value={frame.background}
        onChange={(background) => onPatch(frame.id, { background })}
        palette={theme.palette}
        allowNone
        scaled
        camera={camera}
        title={t('frame.fillColor')}
      />

      <span className="frame-options-divider" />

      <span className="frame-options-label">{t('frame.border')}</span>
      <select
        value={frame.borderWidth}
        onChange={(event) => onPatch(frame.id, { borderWidth: Number(event.target.value) })}
        title={t('frame.borderWidth')}
      >
        {BORDER_WIDTHS.map((width) => (
          <option key={width} value={width}>
            {width === 0 ? t('frame.without') : `${width} px`}
          </option>
        ))}
        {/* Wie bei der Schriftgröße: Beim Mitskalieren entstehen krumme Stärken.
            Ohne diesen Eintrag fiele die Auswahl auf „ohne" zurück und zeigte
            damit etwas an, was gar nicht eingestellt ist. */}
        {!BORDER_WIDTHS.includes(frame.borderWidth) && (
          <option value={frame.borderWidth}>{Math.round(frame.borderWidth)} px</option>
        )}
      </select>
      <ColorPicker
        value={frame.borderColor}
        onChange={(borderColor) =>
          onPatch(frame.id, { borderColor, borderWidth: frame.borderWidth || 2 })
        }
        palette={theme.palette}
        scaled
        camera={camera}
        title={t('frame.borderColor')}
      />

      <span className="frame-options-divider" />

      <span className="frame-options-label" title={t('frame.gridTitle')}>
        {t('frame.grid')}
      </span>
      <select
        value={frame.padding}
        onChange={(event) => onPatch(frame.id, { padding: Number(event.target.value) })}
        title={t('frame.paddingTitle')}
      >
        <option value={FRAME_LAYOUT_AUTO}>
          {t('frame.paddingAuto', {
            percent: Math.round((resolved.padding / Math.min(frame.w, frame.h)) * 100),
          })}
        </option>
        {PADDING_CHOICES.map((share) => (
          <option key={share} value={share}>
            {share === 0
              ? t('frame.paddingNone')
              : t('frame.paddingShare', { percent: Math.round(share * 100) })}
          </option>
        ))}
      </select>
      <select
        value={frame.columns}
        onChange={(event) => onPatch(frame.id, { columns: Number(event.target.value) })}
        title={t('frame.columnsTitle')}
      >
        <option value={FRAME_LAYOUT_AUTO}>{t('frame.auto', { value: resolved.columns })}</option>
        {TRACK_CHOICES.map((count) => (
          <option key={count} value={count}>
            {count === 0 ? t('frame.noColumns') : t('frame.columns', { count })}
          </option>
        ))}
      </select>
      <select
        value={frame.rows}
        onChange={(event) => onPatch(frame.id, { rows: Number(event.target.value) })}
        title={t('frame.rowsTitle')}
      >
        <option value={FRAME_LAYOUT_AUTO}>{t('frame.auto', { value: resolved.rows })}</option>
        {TRACK_CHOICES.map((count) => (
          <option key={count} value={count}>
            {count === 0 ? t('frame.noRows') : t('frame.rows', { count })}
          </option>
        ))}
      </select>

      {onToggleScaleContent && (
        <>
          <span className="frame-options-divider" />
          <button
            type="button"
            className={`frame-options-action${scaleContent ? ' is-on' : ''}`}
            onClick={onToggleScaleContent}
            aria-pressed={scaleContent}
            title={
              scaleContent ? t('frame.scaleOnTitle') : t('frame.scaleOffTitle')
            }
          >
            ⤢ {t('frame.scaleContent')}
          </button>
        </>
      )}

      {onSaveTemplate && (
        <>
          <span className="frame-options-divider" />
          <button
            type="button"
            className="frame-options-action"
            onClick={() => onSaveTemplate(frame)}
            title={t('frame.saveTemplateTitle')}
          >
            {t('frame.saveTemplate')}
          </button>
        </>
      )}
    </div>
  )
}
